// src/pages/BotDetails.js
import React from "react";
import { Link, useParams } from "react-router-dom";
import bots from "../botsData";
import botPageContent from "../botPageContent";
import BotPageSeo from "../components/BotPageSeo";

const secondaryActionClass =
  "px-5 py-3 rounded-lg border border-apb-border-soft text-apb-text-secondary font-semibold inline-flex items-center gap-2 transition duration-240 focus-visible:outline-none focus-ring-neo hover:border-apb-accent-purple hover:text-white hover:bg-white/10";
const primaryActionClass =
  "px-5 py-3 rounded-lg font-semibold inline-flex items-center gap-2 text-sm border border-apb-accent-purple/50 bg-gradient-to-r from-apb-accent-purple/24 to-apb-accent-cyan/24 text-white apb-glow-cyan focus-visible:outline-none focus-ring-neo transition duration-240 hover:border-apb-accent-cyan hover:apb-glow-purple hover:scale-[1.02]";
const voteActionClass =
  "px-5 py-3 rounded-lg font-semibold inline-flex items-center gap-2 text-sm border border-apb-border-soft text-apb-text-secondary bg-white/5 focus-visible:outline-none focus-ring-neo transition duration-240 hover:border-apb-accent-cyan hover:text-white hover:bg-apb-accent-cyan/10";

function ExternalAction({ href, label, className, botName }) {
  if (!href) return null;

  return (
    <a
      className={className}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`${botName} ${label}`}
    >
      {label}
    </a>
  );
}

export default function BotDetails() {
  const { id } = useParams();
  const bot = bots.find((item) => item.id === id);

  if (!bot) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-white relative overflow-hidden">
        <div className="absolute inset-x-0 top-8 left-1/2 h-px w-[80%] -translate-x-1/2 bg-gradient-to-r from-transparent via-apb-accent-cyan to-transparent opacity-70" />
        <div className="text-center apb-surface-glass p-8 rounded-2xl border border-apb-border-soft">
          <p className="text-2xl font-bold text-white apb-glow-text">Bot not found</p>
          <Link
            to="/"
            className="mt-3 inline-block underline text-apb-accent-cyan hover:text-white transition-colors duration-240"
          >
            Back to bots
          </Link>
        </div>
      </div>
    );
  }

  const page = botPageContent[bot.id];
  const links = bot.links || [];
  const docsLink =
    links.find((link) => link.label === "Docs") ||
    (bot.websiteLabel === "Docs" && bot.website
      ? { url: bot.website }
      : page.docsUrl
        ? { url: page.docsUrl }
        : undefined);
  const websiteLink = bot.showWebsiteInShowcase
    ? links.find((link) => link.label === "Website")
    : undefined;

  return (
    <div className="relative min-h-screen py-16 px-4">
      <div className="pointer-events-none absolute inset-x-0 top-6 left-1/2 h-px w-[80%] -translate-x-1/2 bg-gradient-to-r from-transparent via-apb-accent-cyan to-transparent opacity-70" />
      <div className="pointer-events-none absolute inset-x-0 top-20 left-0 h-full w-full">
        <div className="mx-auto h-full max-w-6xl bg-[linear-gradient(to_right,transparent_0%,rgba(255,255,255,0.06)_2px,transparent_2px,transparent_52px),linear-gradient(to_bottom,transparent_0%,rgba(255,255,255,0.06)_2px,transparent_2px,transparent_52px)] bg-[length:52px_52px,52px_52px] opacity-10" />
      </div>
      <div className="max-w-5xl mx-auto relative z-10">
        <div className="mb-12 apb-surface-glass px-6 py-8 rounded-2xl">
          <p className="inline-flex items-center gap-2 rounded-full border border-apb-border-soft bg-black/20 px-4 py-1.5 text-xs uppercase tracking-[0.25em] text-apb-text-secondary">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-apb-accent-cyan apb-glow-cyan" />
            Bot Profile
          </p>
          <h1 className="mt-5 text-3xl md:text-4xl font-extrabold text-white apb-glow-text">{bot.name}</h1>
          <p className="mt-2 max-w-3xl text-apb-text-secondary">{bot.description}</p>
        </div>
      </div>
      <BotPageSeo bot={bot} categories={page.categories} />
      <article className="max-w-5xl mx-auto apb-surface-glass p-6 md:p-8 rounded-2xl border border-apb-border-soft">
        <header className="relative overflow-hidden rounded-xl p-5 apb-surface-panel">
          <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-apb-accent-cyan to-transparent opacity-70" />
          <div className="pointer-events-none absolute right-8 top-3 h-20 w-20 rounded-full bg-apb-accent-cyan/12 blur-[16px]" />
          <div className="flex items-center gap-5">
            <div className="h-20 w-20 md:w-24 md:h-24 rounded-full bg-gradient-to-br from-apb-accent-purple via-apb-accent-cyan to-apb-accent-purple p-[3px] shrink-0 shadow-lg">
              <div className="h-full w-full rounded-full overflow-hidden border border-black/20 bg-apb-bg-surface">
                <img
                  src={bot.image}
                  alt={`${bot.name} logo`}
                  className="w-full h-full object-cover"
                  style={{ transform: `scale(${bot.imageScale ?? 1})` }}
                  loading="lazy"
                  onError={(event) => {
                    event.currentTarget.src = "/images/team-logo.png";
                  }}
                />
              </div>
            </div>
            <div>
              <p className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-apb-text-secondary">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-apb-accent-purple apb-glow-purple" />
                Bot details
              </p>
            </div>
          </div>
        </header>

        <div className="mt-6 grid gap-6 lg:grid-cols-[1.2fr_0.8fr]">
          <div className="space-y-6">
            <section
              className="rounded-2xl border border-apb-border-soft bg-apb-bg-card/65 p-5"
              aria-labelledby="overview-heading"
            >
              <h2 id="overview-heading" className="text-xl font-semibold text-white apb-glow-text">
                Overview
              </h2>
              <p className="mt-2 text-apb-text-secondary">{bot.details || bot.description}</p>
            </section>

            <section
              className="rounded-2xl border border-apb-border-soft bg-apb-bg-card/65 p-5"
              aria-labelledby="best-for-heading"
            >
              <h2 id="best-for-heading" className="text-xl font-semibold text-white apb-glow-text">
                Best for
              </h2>
              <p className="mt-2 text-apb-text-secondary">{page.bestFor}</p>
            </section>

            <section
              className="rounded-2xl border border-apb-border-soft bg-apb-bg-card/65 p-5"
              aria-labelledby="categories-heading"
            >
              <h2 id="categories-heading" className="text-xl font-semibold text-white apb-glow-text">
                Categories
              </h2>
              <div className="mt-3 flex flex-wrap gap-2" aria-label="Bot categories">
                {page.categories.map((category) => (
                  <span
                    key={category}
                    className="rounded-full border border-apb-accent-cyan/35 bg-apb-accent-cyan/8 px-3 py-1 text-sm text-apb-accent-cyan"
                  >
                    {category}
                  </span>
                ))}
              </div>
            </section>
          </div>

          <aside className="rounded-2xl border border-apb-border-soft bg-apb-bg-card/65 p-5">
            <h2 id="actions-heading" className="text-xl font-semibold text-white apb-glow-text">
              Actions
            </h2>
            <nav className="mt-5 flex flex-wrap gap-3" aria-label={`${bot.name} actions`}>
              <ExternalAction
                href={bot.invite}
                label="Invite Bot"
                className={primaryActionClass}
                botName={bot.name}
              />
              <ExternalAction
                href={docsLink?.url}
                label="Docs"
                className={secondaryActionClass}
                botName={bot.name}
              />
              <ExternalAction
                href={bot.voteUrl}
                label="Vote"
                className={voteActionClass}
                botName={bot.name}
              />
              <ExternalAction
                href={websiteLink?.url}
                label="Website"
                className={secondaryActionClass}
                botName={bot.name}
              />
            </nav>
          </aside>
        </div>
      </article>
    </div>
  );
}
